import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  Pagination,
  PaginationItem,
  SelectChangeEvent,
  Theme,
  useMediaQuery,
} from "@mui/material";
import {
  ActionRefundType,
  ENetwork,
  FilterStateRefund,
  Refund,
  RefundStatus,
  SortBy,
} from "@/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DEFAULT_PAGE_SIZE, QUERY_KEY } from "@/constants";
import useDebounce from "@/hooks/useDebounce";
import RefundFilters from "./components/RefundFilters";
import {
  cancellationStatusService,
  fetchListRefundService,
} from "@/services/refund.service";
import { useRefundColumns } from "@/hooks/useRefundColumns";
import RefundTable from "./components/RefundTable";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefundCard from "./components/RefundCard";
import { DateRange } from "react-day-picker";
import SkeletonRefundCard from "./components/SkeletonRefundCard";
import ReviewRefundRequestModal from "./components/ReviewRefundRequestModal";
import CompleteRefundModal from "./components/CompleteRefundModal";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { CircleCheck, Info } from "lucide-react";
import RefundTableSkeleton from "./components/RefundTableSkeleton";
const RefundPage = () => {
  const [page, setPage] = useState<number>(1);
  const initialFilterState: FilterStateRefund = {
    sortBy: null,
    sortOrder: null,
    network: null,
    action: null,
    startDate: null,
    endDate: null,
  };
  const [filterValues, setFilterValues] = useState<{
    searchTerm: string;
    startDate: string | undefined;
    endDate: string | undefined;
    network: ENetwork | string;
    action: ActionRefundType | string;
  }>({
    searchTerm: "",
    startDate: undefined,
    endDate: undefined,
    network: "",
    action: "",
  });
  const [filter, setFilter] = useState<FilterStateRefund>(initialFilterState);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  const debouncedSearchTerm = useDebounce(filterValues.searchTerm, 500);

  const [selectedRefund, setSelectedRefund] = useState<Refund | null>(null);
  const [openReviewRefundRequestModal, setOpenReviewRefundRequestModal] =
    useState(false);

  const [openCompleteRefundModal, setOpenCompleteRefundModal] = useState(false);
  const {
    data: RefundList,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      QUERY_KEY.fetchRefundList,
      {
        page,
        query: debouncedSearchTerm,
        startDate: filter.startDate,
        endDate: filter.endDate,
        network: filter.network,
        refundStatus: filter.action,
        sortBy: filter.sortBy || undefined,
        sortOrder: filter.sortOrder || undefined,
      },
    ],
    queryFn: () =>
      fetchListRefundService({
        page,
        size: DEFAULT_PAGE_SIZE,
        query: debouncedSearchTerm,
        startDate: filter.startDate
          ? dayjs(filter.startDate).startOf("day").toISOString()
          : undefined,
        endDate: filter.endDate
          ? dayjs(filter.endDate).endOf("day").toISOString()
          : undefined,
        network:
          (filter.network === ENetwork.ALL ? undefined : filter.network) ||
          undefined,
        refundStatus:
          (filter.action === ActionRefundType.ALL_ACTION
            ? undefined
            : filter.action) || undefined,
        sortBy: filter.sortBy || undefined,
        sortOrder: filter.sortOrder || undefined,
      }),
    staleTime: 5000,
  });

  const cancelRefund = useMutation({
    mutationFn: cancellationStatusService,
    onSuccess: (data) => {
      switch (data.status) {
        case RefundStatus.REJECTED:
          // toast.success("Rejected refund successfully!");
          break;
        case RefundStatus.APPROVED:
          toast.success(
            `Refund for booking ID ${data.bookingCode} is now processing`,
            {
              style: {
                backgroundColor: "#FEFCE8",
                color: "#CA8A04",
                border: "1px solid #FDE047",
                borderRadius: "8px",
                padding: "16px",
                fontSize: "16px",
                fontWeight: "500",
              },
              icon: (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#CA8A04",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    padding: "10px",
                    marginRight: "12px",
                  }}
                >
                  <Info size={22} color="#fff" />
                </span>
              ),
              iconTheme: {
                primary: "#FACC15",
                secondary: "#FEF9C3",
              },
            },
          );
          break;
        case RefundStatus.COMPLETED:
          toast.success(
            `Refund for booking ID ${data.bookingCode} is now completed`,
            {
              style: {
                backgroundColor: "#ECFDF5",
                color: "#059669",
                border: "1px solid #6EE7B7",
                borderRadius: "8px",
                padding: "16px",
                fontSize: "16px",
                fontWeight: "500",
              },
              icon: (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#10B981",
                    borderRadius: "6px",
                    width: 32,
                    height: 32,
                    padding: "10px",
                    marginRight: "12px",
                  }}
                >
                  <CircleCheck size={22} color="#fff" />
                </span>
              ),
              iconTheme: {
                primary: "#34D399",
                secondary: "#ECFDF5",
              },
            },
          );
          break;
        case RefundStatus.PENDING:
          // toast.success("Pending refund successfully!");
          break;
        default:
          // toast.success("Success!");
          break;
      }
      refetch();
    },
    onError: () => {
      toast.error("Error!");
    },
  });

  const handleChangePage = useCallback(
    (_: ChangeEvent<unknown>, pageNext: number) => {
      setPage(pageNext);
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, 100);
    },
    [],
  );

  const handleNetworkChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value as ENetwork | "";
    setFilterValues((prev) => ({ ...prev, network: newValue }));
    if (isMobile) {
      setFilter((prev) => ({
        ...prev,
        network: newValue === "" ? null : (newValue as ENetwork),
      }));

      setPage(1);
    }
  };
  const handleActionChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value as ActionRefundType | "";
    setFilterValues((prev) => ({ ...prev, action: newValue }));
    if (isMobile) {
      setFilter((prev) => ({
        ...prev,
        action: newValue === "" ? null : (newValue as ActionRefundType),
      }));
      setPage(1);
    }
  };

  const handleSearchChange = (value: string) => {
    setFilterValues((prev) => ({ ...prev, searchTerm: value }));

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setFilter((prev) => ({ ...prev }));
      setPage(1);
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleDateChange = (newDate: DateRange) => {
    setFilterValues((prev) => ({
      ...prev,
      startDate: newDate.from ? newDate.from.toISOString() : undefined,
      endDate: newDate.to ? newDate.to.toISOString() : undefined,
    }));

    if (isMobile) {
      setFilter((prev) => ({
        ...prev,
        startDate: newDate.from ? newDate.from?.toISOString() : null,
        endDate: newDate.to ? newDate.to?.toISOString() : null,
      }));
      setPage(1);
    }
  };

  const handleApplyFilter = useCallback(() => {
    if (filterValues.startDate && filterValues.endDate) {
      const startDate = new Date(filterValues.startDate);
      const endDate = new Date(filterValues.endDate);

      if (startDate > endDate) {
        return;
      }
    }

    setFilter((prev) => ({
      ...prev,
      network: (filterValues.network as ENetwork) || null,
      action: (filterValues.action as ActionRefundType) || null,
      startDate: filterValues.startDate || null,
      endDate: filterValues.endDate || null,
      sortBy: prev.sortBy,
      sortOrder: prev.sortOrder,
    }));

    setPage(1);
    refetch();
  }, [filterValues, refetch]);

  const handleResetFilter = useCallback(() => {
    setFilterValues({
      searchTerm: "",
      startDate: undefined,
      endDate: undefined,
      network: "",
      action: "",
    });

    setFilter(initialFilterState);
    setPage(1);
    refetch();
  }, [refetch]);

  const setSortDiscount = ({ column, direction }: SortBy) => {
    setFilter({
      ...filter,
      sortBy: column as FilterStateRefund["sortBy"],
      sortOrder: direction as FilterStateRefund["sortOrder"],
    });
  };

  const onAction = (refund: Refund, status: RefundStatus) => {
    setSelectedRefund(refund);

    switch (status) {
      case RefundStatus.PENDING:
        setOpenReviewRefundRequestModal(true);
        break;
      case RefundStatus.APPROVED:
        setOpenCompleteRefundModal(true);
        break;
      case RefundStatus.REJECTED:
        setOpenReviewRefundRequestModal(true);
        break;
      case RefundStatus.COMPLETED:
        setOpenCompleteRefundModal(true);
        break;
      default:
        setOpenReviewRefundRequestModal(true);
        break;
    }
  };

  const handleCloseModal = () => {
    setOpenReviewRefundRequestModal(false);
    setOpenCompleteRefundModal(false);

    setTimeout(() => {
      setSelectedRefund(null);
    }, 300);
  };

  const handleConfirmRefund = () => {
    cancelRefund.mutate({
      id: selectedRefund?.id ?? "",
      status: RefundStatus.APPROVED,
    });
    refetch();
    handleCloseModal();
  };

  const handleRejectRefund = () => {
    cancelRefund.mutate({
      id: selectedRefund?.id ?? "",
      status: RefundStatus.REJECTED,
    });
    handleCloseModal();
  };

  const handleSubmitRefund = (transactionId: string) => {
    cancelRefund.mutate({
      id: selectedRefund?.id ?? "",
      status: RefundStatus.COMPLETED,
      transactionId: transactionId ?? "",
    });
    handleCloseModal();
  };

  const discountColumns = useRefundColumns({
    data: RefundList?.data ?? [],
    onAction,
  });

  return (
    <section>
      <div className="flex flex-col max-sm:mt-3 max-sm:w-full max-sm:flex-1 max-sm:flex-row max-sm:items-center max-sm:justify-between">
        <div className="-mt-16 flex items-center gap-4 max-sm:mt-0 md:w-1/2">
          <h2 className="text-3xl font-semibold text-text max-sm:text-[18px]">
            Hotel Booking Refund
          </h2>
        </div>
      </div>
      <div className="md:hidden">
        <div>
          <RefundFilters
            network={filterValues.network}
            onNetworkChange={handleNetworkChange}
            action={filterValues.action}
            onActionChange={handleActionChange}
            startDate={filterValues.startDate}
            endDate={filterValues.endDate}
            onDateChange={handleDateChange}
            searchTerm={filterValues.searchTerm}
            onSearch={handleSearchChange}
            onApply={handleApplyFilter}
            onReset={handleResetFilter}
          />
        </div>

        <div className="mt-4">
          {isLoading ? (
            <>
              <SkeletonRefundCard />
              <SkeletonRefundCard />
              <SkeletonRefundCard />
            </>
          ) : RefundList?.data && RefundList?.data.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 ">
              {RefundList?.data.map((item, idx) => (
                <div key={idx}>
                  <RefundCard refund={item} onAction={onAction} />
                </div>
              ))}
            </div>
          ) : (
            <div className="flex h-[300px] w-full items-center justify-center rounded-3xl bg-white text-text">
              <span className="text-lg font-semibold text-text">
                Data not found
              </span>
            </div>
          )}
        </div>

        {RefundList?.data && RefundList.data.length > 0 && (
          <div className="flex w-full items-center justify-center">
            <Pagination
              shape="rounded"
              className="my-[12px] mt-[24px] self-center"
              page={page}
              count={RefundList?.totalPages ?? 0}
              onChange={handleChangePage}
              siblingCount={0}
              boundaryCount={3}
              renderItem={(item) => (
                <PaginationItem
                  slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                  sx={{
                    backgroundColor: "transparent",
                    color: "#94A3B8",
                    fontSize: 16,
                    "&.Mui-selected": {
                      backgroundColor: "#E0E7FF",
                      color: "#4F46E5",
                      fontWeight: "bold",
                    },
                    "&.Mui-disabled": {
                      opacity: 0.5,
                    },
                  }}
                  {...item}
                />
              )}
            />
          </div>
        )}
      </div>
      <div className="flex flex-col space-y-8 text-[#1E293B] max-sm:hidden">
        <RefundFilters
          network={filterValues.network}
          onNetworkChange={handleNetworkChange}
          action={filterValues.action}
          onActionChange={handleActionChange}
          startDate={filterValues.startDate}
          endDate={filterValues.endDate}
          onDateChange={handleDateChange}
          searchTerm={filterValues.searchTerm}
          onSearch={handleSearchChange}
          onApply={handleApplyFilter}
          onReset={handleResetFilter}
        />

        {isLoading ? (
          <RefundTableSkeleton />
        ) : (
          <RefundTable
            rows={discountColumns}
            labels={[
              "Booking ID & Date",
              "Contact person",
              "Amount of refund",
              "Address to refund",
              "Request Date",
              "Cancellation Policy",
              "Action",
            ]}
            page={page}
            count={RefundList?.totalPages ?? 0}
            onChange={handleChangePage}
            setSort={setSortDiscount}
          />
        )}
      </div>
      {selectedRefund && (
        <ReviewRefundRequestModal
          open={openReviewRefundRequestModal}
          onClose={handleCloseModal}
          onConfirm={handleConfirmRefund}
          onReject={handleRejectRefund}
          selectRefund={selectedRefund}
        />
      )}
      <CompleteRefundModal
        open={openCompleteRefundModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitRefund}
        txId={selectedRefund?.transactionId}
        destination={{
          network: selectedRefund?.refundNetwork ?? "",
          walletAddress: selectedRefund?.walletAddress ?? "",
          bankName: selectedRefund?.bankName || "",
          bankAccountName: selectedRefund?.bankAccountName || "",
          bankAccountNumber: selectedRefund?.bankAccountNumber || ""
        }}
      />
    </section>
  );
};

export default RefundPage;
